import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { toast } from 'react-toastify';
import AccountCircleOutlinedIcon from '@mui/icons-material/AccountCircleOutlined';
import SaveOutlinedIcon from '@mui/icons-material/SaveOutlined';
import PhotoCameraOutlinedIcon from '@mui/icons-material/PhotoCameraOutlined';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import { useTranslation } from 'react-i18next';
import BasicCli from '../../main/components/basicCli/BasicCli';
import { ApiConstants } from '../../main/constants/ApiConstants';
import useGetUser from '../../main/hooks/useGetUser';
import axiosInstance from '../../main/initializers/axios';
import { setUser } from '../../main/store/stores/user/user.store';
import { buildEntityFileUrl } from '../../main/utils/fileUrl';
import './UserProfile.css';

type ProfileForm = {
  firstName: string;
  lastName: string;
  userName: string;
  email: string;
  operatorCode: string;
};

type PasswordForm = {
  currentPassword: string;
  newPassword: string;
  confirmPassword: string;
};

const toProfileForm = (user: any): ProfileForm => ({
  firstName: user?.firstName ?? '',
  lastName: user?.lastName ?? '',
  userName: user?.userName ?? '',
  email: user?.email ?? '',
  operatorCode: user?.operatorCode ?? '',
});

const emptyPasswordForm: PasswordForm = {
  currentPassword: '',
  newPassword: '',
  confirmPassword: '',
};

const UserProfile = () => {
  const { i18n } = useTranslation();
  const user = useGetUser();
  const dispatch = useDispatch();
  const [form, setForm] = useState<ProfileForm>(() => toProfileForm(user));
  const [passwordForm, setPasswordForm] = useState<PasswordForm>(emptyPasswordForm);
  const [avatarBase64, setAvatarBase64] = useState<string>(user?.avatar ?? '');
  const [avatarPreview, setAvatarPreview] = useState<string>('');
  const [saving, setSaving] = useState(false);
  const [passwordSaving, setPasswordSaving] = useState(false);

  const displayName = `${form.firstName} ${form.lastName}`.trim() || form.userName || i18n.t('profile.title');
  const companyName = user?.companyName || user?.CompanyName || i18n.t('profile.companyFallback');
  const storedAvatarUrl = user?.id ? buildEntityFileUrl(user.id, ApiConstants.EntityTypes.User) : '';
  const avatarSrc = avatarPreview || avatarBase64 || storedAvatarUrl;
  const avatarInitial = (displayName || 'U').trim().charAt(0).toUpperCase();

  useEffect(() => {
    setForm(toProfileForm(user));
    setAvatarBase64(user?.avatar ?? '');
    setAvatarPreview('');
  }, [user]);

  const updateField = (field: keyof ProfileForm, value: string) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const updatePasswordField = (field: keyof PasswordForm, value: string) => {
    setPasswordForm((prev) => ({ ...prev, [field]: value }));
  };

  const fileToBase64 = (file: File) =>
    new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(String(reader.result));
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });

  const onAvatarChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error(i18n.t('profile.messages.imageOnly'));
      return;
    }

    if (file.size > 2 * 1024 * 1024) {
      toast.error(i18n.t('profile.messages.imageTooLarge'));
      return;
    }

    const base64 = await fileToBase64(file);
    setAvatarBase64(base64);
    setAvatarPreview(base64);
  };

  const onSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!user?.id) return;

    setSaving(true);
    try {
      const payload = {
        Id: user.id,
        RoleId: user.roleId,
        Email: form.email.trim(),
        CanPrintFiscal: user.canPrintFiscal ?? false,
        IsActive: user.isActive ?? true,
        OperatorCode: form.operatorCode.trim(),
        PageRedirect: Number(user.pageRedirect ?? 2),
        UserName: form.userName.trim(),
        FirstName: form.firstName.trim(),
        LastName: form.lastName.trim(),
        Avatar: avatarBase64,
      };

      const response = await axiosInstance.post(ApiConstants.User.update, payload);
      const responseUser = response.data?.user ?? response.data?.data ?? response.data;
      const nextUser = {
        ...user,
        ...responseUser,
        id: user.id,
        roleId: payload.RoleId,
        email: payload.Email,
        userName: payload.UserName,
        firstName: payload.FirstName,
        lastName: payload.LastName,
        operatorCode: payload.OperatorCode,
        avatar: payload.Avatar,
      };

      dispatch(setUser(nextUser));
      toast.success(i18n.t('profile.messages.profileUpdated'));
    } catch {
      toast.error(i18n.t('profile.messages.profileUpdateFailed'));
    } finally {
      setSaving(false);
    }
  };

  const onPasswordSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!user?.id) return;

    const currentPassword = passwordForm.currentPassword.trim();
    const newPassword = passwordForm.newPassword.trim();
    const confirmPassword = passwordForm.confirmPassword.trim();

    if (!currentPassword || !newPassword || !confirmPassword) {
      toast.error(i18n.t('profile.messages.passwordFieldsRequired'));
      return;
    }

    if (newPassword.length < 8) {
      toast.error(i18n.t('profile.messages.passwordTooShort'));
      return;
    }

    if (newPassword !== confirmPassword) {
      toast.error(i18n.t('profile.messages.passwordMismatch'));
      return;
    }

    if (newPassword === currentPassword) {
      toast.error(i18n.t('profile.messages.passwordSameAsCurrent'));
      return;
    }

    setPasswordSaving(true);
    try {
      await axiosInstance.post(ApiConstants.User.changePassword, {
        userId: user.id,
        oldPassword: currentPassword,
        newPassword,
        confirmPassword,
      });

      setPasswordForm(emptyPasswordForm);
      toast.success(i18n.t('profile.messages.passwordChanged'));
    } catch {
      toast.error(i18n.t('profile.messages.passwordChangeFailed'));
    } finally {
      setPasswordSaving(false);
    }
  };

  return (
    <BasicCli>
      <div className="profilePage bcPageContent">
        <div className="bcPageHeader profileHeader">
          <div>
            <h1 className="bcPageTitle">{i18n.t('profile.title')}</h1>
            <p className="profileSubtitle">{i18n.t('profile.subtitle')}</p>
          </div>
        </div>

        <div className="profileGrid">
          <section className="profileSummary" aria-label={i18n.t('profile.userSummary')}>
            <div className="profileAvatarWrap">
              {avatarSrc ? (
                <img src={avatarSrc} alt={displayName} className="profileAvatar" onError={() => setAvatarPreview('')} />
              ) : (
                <div className="profileAvatar profileAvatarFallback" aria-hidden="true">
                  {avatarInitial}
                </div>
              )}
              <label className="profileAvatarButton" title={i18n.t('profile.changePhoto')}>
                <PhotoCameraOutlinedIcon fontSize="small" />
                <input type="file" accept="image/*" onChange={onAvatarChange} />
              </label>
            </div>

            <div className="profileSummaryText">
              <h2>{displayName}</h2>
              <span>{form.email || i18n.t('profile.noEmail')}</span>
            </div>

            <div className="profileFacts">
              <div>
                <span>{i18n.t('profile.company')}</span>
                <strong>{companyName}</strong>
              </div>
              <div>
                <span>{i18n.t('profile.role')}</span>
                <strong>{user?.role?.name ?? user?.roleName ?? `#${user?.roleId ?? '-'}`}</strong>
              </div>
            </div>
          </section>

          <div className="profileMainColumn">
            <form className="profileForm" onSubmit={onSubmit} autoComplete="off">
              <div className="profileFormTitle">
                <AccountCircleOutlinedIcon fontSize="small" />
                <h2>{i18n.t('profile.userData')}</h2>
              </div>

              <div className="profileFields">
                <label>
                  <span>{i18n.t('profile.firstName')}</span>
                  <input value={form.firstName} onChange={(e) => updateField('firstName', e.target.value)} />
                </label>
                <label>
                  <span>{i18n.t('profile.lastName')}</span>
                  <input value={form.lastName} onChange={(e) => updateField('lastName', e.target.value)} />
                </label>
                <label>
                  <span>{i18n.t('profile.username')}</span>
                  <input value={form.userName} onChange={(e) => updateField('userName', e.target.value)} />
                </label>
                <label>
                  <span>{i18n.t('profile.email')}</span>
                  <input type="email" value={form.email} onChange={(e) => updateField('email', e.target.value)} />
                </label>
                <label className="profileFieldWide">
                  <span>{i18n.t('profile.operatorCode')}</span>
                  <input value={form.operatorCode} onChange={(e) => updateField('operatorCode', e.target.value)} />
                </label>
              </div>

              <div className="profileActions">
                <button type="submit" className="ui-control ui-btn ui-btn--primary profileSaveBtn" disabled={saving}>
                  <SaveOutlinedIcon fontSize="small" />
                  {saving ? i18n.t('profile.saving') : i18n.t('profile.save')}
                </button>
              </div>
            </form>

            <form className="profileForm" onSubmit={onPasswordSubmit} autoComplete="off">
              <div className="profileFormTitle">
                <LockOutlinedIcon fontSize="small" />
                <h2>{i18n.t('profile.changePassword')}</h2>
              </div>

              <div className="profileFields">
                <label>
                  <span>{i18n.t('profile.currentPassword')}</span>
                  <input
                    type="password"
                    value={passwordForm.currentPassword}
                    onChange={(e) => updatePasswordField('currentPassword', e.target.value)}
                    autoComplete="current-password"
                  />
                </label>
                <label>
                  <span>{i18n.t('profile.newPassword')}</span>
                  <input
                    type="password"
                    value={passwordForm.newPassword}
                    onChange={(e) => updatePasswordField('newPassword', e.target.value)}
                    autoComplete="new-password"
                  />
                </label>
                <label className="profileFieldWide">
                  <span>{i18n.t('profile.confirmPassword')}</span>
                  <input
                    type="password"
                    value={passwordForm.confirmPassword}
                    onChange={(e) => updatePasswordField('confirmPassword', e.target.value)}
                    autoComplete="new-password"
                  />
                </label>
              </div>

              <div className="profileActions">
                <button type="submit" className="ui-control ui-btn ui-btn--primary profileSaveBtn" disabled={passwordSaving}>
                  <SaveOutlinedIcon fontSize="small" />
                  {passwordSaving ? i18n.t('profile.saving') : i18n.t('profile.changePassword')}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </BasicCli>
  );
};

export default UserProfile;
